import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import useClassesStore from '../stateManagers/store/useClassesStore';

interface FilterState {
  selectedClass: string;
  selectedSection: string;
  selectedMonth: string;
  selectedStatus: string;
}

interface SearchFilterBarProps {
  onApplyFilters: (filters: FilterState) => void;
  sessionMonths?: string[];
  session?: string | null;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const buildMonthList = (
  sessionMonths: string[],
  session: string | null
): { name: string; year: string }[] => {
  const years = session ? session.split('-') : [];
  const yearA = years[0] ?? String(new Date().getFullYear());
  // Expand a 2-digit end year ("26") against the start year's century
  // ("2025" -> "2026"), matching FeesPanel's buildSessionMonthYearMap.
  const yearB = years[1]
    ? years[1].length === 2
      ? yearA.slice(0, 2) + years[1]
      : years[1]
    : yearA;

  const indices = sessionMonths.map(m =>
    MONTH_NAMES.findIndex(mn => mn.toLowerCase() === m.toLowerCase())
  );

  let wrapped = false;
  return sessionMonths.map((name, i) => {
    if (i > 0 && indices[i] < indices[i - 1]) wrapped = true;
    return { name, year: wrapped ? yearB : yearA };
  });
};

// ─────────────────────────────────────────────────────────────────────────
// Shared chip-button style, mirrors ClassSectionFilter's look
// ─────────────────────────────────────────────────────────────────────────

const chipStyle = (active: boolean, activeColor: string, disabled = false): React.CSSProperties => ({
  padding: '6px 16px',
  border: active ? `1.5px solid ${activeColor}` : '1.5px solid #e5e7eb',
  borderRadius: '8px',
  background: active
    ? `linear-gradient(135deg, ${activeColor}dd 0%, ${activeColor} 100%)`
    : 'white',
  color: active ? 'white' : '#1f2937',
  fontSize: '11.5px',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  outline: 'none',
  boxShadow: active ? `0 2px 8px ${activeColor}40` : 'none',
  opacity: disabled ? 0.5 : 1,
  transition: 'all 0.2s ease',
});

const sectionLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11.5px',
  fontWeight: 700,
  color: '#1e40af',
  marginBottom: '10px',
  letterSpacing: '0.02em',
};

const CLASS_COLOR = '#3b82f6';
const SECTION_COLOR = '#0d9488';
const MONTH_COLOR = '#4169E1';
const STATUS_COLORS: Record<string, string> = {
  All: '#6b7280',
  Paid: '#10b981',
  Unpaid: '#ef4444',
};

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  onApplyFilters,
  sessionMonths,
  session,
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');

  const toast = useRef<Toast>(null);
  const { classOptions, fetchClasses, isFetching, hasFetched } = useClassesStore();

  useEffect(() => {
    if (!hasFetched && !isFetching) fetchClasses(false);
  }, [hasFetched, isFetching, fetchClasses]);

  // Initialize the default month once per session, not on every parent
  // re-render. `sessionMonths` is a new array reference on each FeesPanel
  // render, so we key off `session` (a stable string) plus the joined
  // month list instead of the array identity — and skip entirely once a
  // month has already been picked/initialized, so this never clobbers the
  // user's selection.
  const sessionMonthsKey = sessionMonths?.join(',') ?? '';
  const initializedForKey = useRef<string | null>(null);

  useEffect(() => {
    const key = `${session ?? ''}|${sessionMonthsKey}`;
    if (initializedForKey.current === key) return;
    initializedForKey.current = key;

    if (sessionMonths && sessionMonths.length > 0 && session) {
      const list = buildMonthList(sessionMonths, session);
      setSelectedMonth(`${list[0].name} ${list[0].year}`);
    } else {
      const now = new Date();
      setSelectedMonth(`${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionMonthsKey]);

  const months = sessionMonths && sessionMonths.length > 0 && session
    ? buildMonthList(sessionMonths, session)
    : MONTH_NAMES.map(name => ({
        name,
        year: String(new Date().getFullYear()),
      }));

  const statusList = ['All', 'Paid', 'Unpaid'];
  const selectedClassData = classOptions.find((c) => c.id === selectedClass);
  const sections = selectedClassData?.sections || [];
  const hasSections = sections.length > 0;

  const handleClassChange = (classId: string) => {
    if (selectedClass === classId) {
      setSelectedClass('');
      setSelectedSection('');
    } else {
      setSelectedClass(classId);
      setSelectedSection('');
    }
  };

  const handleSectionChange = (section: string) => {
    setSelectedSection(prev => (prev === section ? '' : section));
  };

  const handleMonthChange = (monthValue: string) => {
    setSelectedMonth(monthValue);
  };

  const handleApplyFilters = () => {
    const selectedClassLabel = classOptions.find((c) => c.id === selectedClass)?.label || '';
    onApplyFilters({ selectedClass: selectedClassLabel, selectedSection, selectedMonth, selectedStatus });
    toast.current?.show({ severity: 'success', summary: 'Filters Applied', detail: 'Fee records have been filtered successfully', life: 3000 });
  };

  const handleClearAll = () => {
    const defaultMonth = months.length > 0 ? `${months[0].name} ${months[0].year}` : '';
    setSelectedMonth(defaultMonth);
    setSelectedStatus('All');
    setSelectedClass('');
    setSelectedSection('');
    onApplyFilters({ selectedClass: '', selectedSection: '', selectedMonth: defaultMonth, selectedStatus: 'All' });
    toast.current?.show({ severity: 'info', summary: 'Filters Cleared', detail: 'Optional filters reset.', life: 3000 });
  };

  const hasOptionalFilters = selectedStatus !== 'All' || !!selectedClass || !!selectedSection;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .chip-button {
          transition: all 0.2s ease;
        }
        .chip-button:hover:not(:disabled) {
          transform: translateY(-1px);
        }
        .chip-button:active:not(:disabled) {
          transform: translateY(0);
        }
      `}</style>

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
        border: '1px solid rgba(59, 130, 246, 0.12)',
      }}>

        {/* Class / Section / Month / Status — side by side columns */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'nowrap' }}>

          {/* Class (+ Section stacked below it) */}
          <div style={{ flex: '0 1 calc(55% - 10px)', minWidth: 0 }}>
            <label style={sectionLabelStyle}>Select Class</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {isFetching ? (
                <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>
                  Loading classes...
                </div>
              ) : classOptions.length === 0 ? (
                <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>
                  No classes available
                </div>
              ) : (
                classOptions.map((cls) => (
                  <button
                    key={cls.id}
                    className="chip-button"
                    onClick={() => handleClassChange(cls.id)}
                    disabled={isFetching}
                    style={chipStyle(selectedClass === cls.id, CLASS_COLOR, isFetching)}
                  >
                    {cls.label}
                  </button>
                ))
              )}
            </div>

            {/* Section — only when the selected class has sections */}
            {hasSections && (
              <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f3f4f6' }}>
                <label style={sectionLabelStyle}>Select Section</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {sections.map((section) => (
                    <button
                      key={section}
                      className="chip-button"
                      onClick={() => handleSectionChange(section)}
                      style={chipStyle(selectedSection === section, SECTION_COLOR)}
                    >
                      Section {section}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Month (+ Status stacked below it) */}
          <div style={{ flex: '0 1 calc(45% - 10px)', minWidth: 0, paddingLeft: '20px', borderLeft: '1px solid #f3f4f6' }}>
            <label style={sectionLabelStyle}>
              Select Month <span style={{ color: '#ef4444', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>*required</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {months.map((month) => {
                const monthValue = `${month.name} ${month.year}`;
                return (
                  <button
                    key={monthValue}
                    className="chip-button"
                    onClick={() => handleMonthChange(monthValue)}
                    style={chipStyle(selectedMonth === monthValue, MONTH_COLOR)}
                  >
                    {month.name.substring(0, 3)} {month.year}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f3f4f6' }}>
              <label style={sectionLabelStyle}>Payment Status</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {statusList.map((status) => (
                  <button
                    key={status}
                    className="chip-button"
                    onClick={() => setSelectedStatus(status)}
                    style={chipStyle(selectedStatus === status, STATUS_COLORS[status])}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
          {hasOptionalFilters && (
            <button
              onClick={handleClearAll}
              className="chip-button"
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1.5px solid #e5e7eb',
                background: 'white',
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
                cursor: 'pointer',
              }}
            >
              Clear All
            </button>
          )}
          <button
            onClick={handleApplyFilters}
            className="chip-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 18px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
              fontSize: '12px',
              fontWeight: 700,
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(65, 105, 225, 0.3)',
            }}
          >
            <i className="pi pi-check" style={{ fontSize: '12px' }} />
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

export default SearchFilterBar;